/*
 * File: LocalitySelector.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 12:53 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

// Utilities
import { reduceToBaseURL } from '../../../utilities/utilities';

// Store
import { updateCountryId, updateLocaleId } from '../../store/slices/locale/locale.slice';

// Components
import { Typography } from '../Typography';
import { LocaleLink } from '../LocaleLink';

// Constants
import {
	AVAILABLE_COUNTRIES, AVAILABLE_I18N_NAMESPACES, COUNTRY_OPTIONS, LANGUAGE_OPTIONS
} from '../../../constants/base.constants';

// Styles
import * as S from './LocalitySelector.styles';


/**
 * Component
 */

export const LocalitySelector = ({
	className, forceCompact
}) => {

	// Use hooks
	const location = useLocation();
	const dispatch = useDispatch();

	// Get current locale from hook
	const { localeId, countryId } = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get translation hook
	const { t, i18n } = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Create base path
	const basePath = useMemo(() => reduceToBaseURL(location.pathname), [location]);

	// Handle update country
	const updateCountry = (code) => {
		dispatch(updateCountryId({
			countryId: code,
			partner
		}));
	};

	// Handle update language
	const updateLang = (code) => {

		// Change i18n language
		i18n.changeLanguage(code);

		// Update language id
		dispatch(updateLocaleId({
			localeId: code,
			localeBaseId: code.split('-')[0],
			localePath: `/${code.split('-')[0]}`,
			partner
		}));
	};

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Wrapper className={className} $forceCompact={forceCompact}>
			<S.LocalitySelectorContainer $forceCompact={forceCompact}>

				{/* Instructions Label */}
				<Typography tag="p">
					<Trans t={t} i18nKey="localitySelector.showResources">
						Show resources for
					</Trans>
				</Typography>

				{/* Country Selector */}
				<S.LocalitySelector $forceCompact={forceCompact}>

					{/* Selected Country */}
					<S.CountrySelector className="animate">
						<S.FlagImg
							src={`https://ipdata.co/flags/${countryId}.png`}
							alt={`${COUNTRY_OPTIONS.find(({ code }) => code === countryId.toUpperCase())?.name} flag`}
							title={`${COUNTRY_OPTIONS.find(({ code }) => code === countryId.toUpperCase())?.name} flag`}
						/>
						<Typography tag="span">{COUNTRY_OPTIONS.find(({ code }) => code === countryId.toUpperCase())?.name}</Typography>
						<S.SecondaryIcon className="animate" icon={['far', 'angle-down']} />
					</S.CountrySelector>

					{/* Country Dropdown */}
					<S.Dropdown className="animate">
						{AVAILABLE_COUNTRIES.map((c) => {
							if (c !== countryId) {
								return (
									<LocaleLink
										title={COUNTRY_OPTIONS.find(({ code }) => code === c.toUpperCase())?.name}
										className="animate"
										to={`${basePath}?country=${c}`}
										onClick={() => { updateCountry(c); }}
										disableLocale
										key={c}
									>
										<S.FlagImg
											src={`https://ipdata.co/flags/${c}.png`}
											alt={`${COUNTRY_OPTIONS.find(({ code }) => code === c.toUpperCase())?.name} flag`}
											title={`${COUNTRY_OPTIONS.find(({ code }) => code === c.toUpperCase())?.name} flag`}
										/>
										<Typography tag="span">{COUNTRY_OPTIONS.find(({ code }) => code === c.toUpperCase())?.name}</Typography>
									</LocaleLink>
								);
							}
							return null;
						})}
					</S.Dropdown>
				</S.LocalitySelector>

				{/* Language Selector */}
				<S.LocalitySelector $forceCompact={forceCompact}>

					{/* Selected Language */}
					<S.LanguageSelector className="animate">
						<S.PrimaryIcon icon={['far', 'language']} />
						<Typography tag="span">{LANGUAGE_OPTIONS.find(({ code }) => code === localeId)?.name || 'English'}</Typography>
						<S.SecondaryIcon className="animate" icon={['far', 'angle-down']} />
					</S.LanguageSelector>

					{/* Language Dropdown */}
					<S.Dropdown className="animate">
						{LANGUAGE_OPTIONS.map(({ name, code }) => (
							<LocaleLink
								title={name}
								className="animate"
								to={`/${code}${basePath}`}
								onClick={() => { updateLang(code); }}
								disableLocale
								key={name}
							>
								<Typography tag="span">{name}</Typography>
							</LocaleLink>
						))}
					</S.Dropdown>
				</S.LocalitySelector>
			</S.LocalitySelectorContainer>
		</S.Wrapper>
	);
};


/**
 * Configuration
 */

LocalitySelector.displayName = 'LocalitySelector';
LocalitySelector.propTypes = {
	className: PropTypes.string,
	forceCompact: PropTypes.bool
};
LocalitySelector.defaultProps = {
	className: null,
	forceCompact: false
};
